import { Auth0Client, GetTokenSilentlyVerboseResponse, RedirectLoginResult } from '@auth0/auth0-spa-js'
import { IRedirectLoginAppState } from '@/retail/contexts/user/domain/IRedirectLoginAppState'
import { IClientsPermissions } from '../domain/IClientsPermissions'
import { DELETE_CLIENTS, READ_CLIENTS, REQUESTED_RETAIL_SCOPES, UPDATE_CLIENTS } from '../domain/constants/SCOPES'

import { IAuthenticationRepository } from '../domain/IAuthenticationRepository'
import { User } from '../domain/User'

export class AuthenticationRepository implements IAuthenticationRepository {
  private auth0Client?: Auth0Client

  // Lazily instantiated so that merely importing the retail DI chain (which the
  // b2ag context does transitively through the route guard / layouts) does not
  // create an Auth0 client. The client is only built when a retail auth method
  // is actually called — i.e. only in the Retail context.
  get client(): Auth0Client {
    if (!this.auth0Client) {
      this.auth0Client = new Auth0Client({
        domain: window.env.AUTH0_DOMAIN!,
        client_id: window.env.AUTH0_CLIENT_ID!,
        redirect_uri: `${window.location.origin}/oauth/callback`,
        audience: window.env.AUTH0_AUDIENCE!,
        cacheLocation: 'localstorage',
        advancedOptions: {
          defaultScope: 'openid',
        },
        scope: REQUESTED_RETAIL_SCOPES.join(' '),
      })
    }
    return this.auth0Client
  }

  async loginWithRedirect(params): Promise<void> {
    return this.client.loginWithRedirect(params)
  }

  async logout(): Promise<void> {
    return this.client.logout({
      returnTo: window.location.origin,
    })
  }

  async handleRedirect(): Promise<RedirectLoginResult<IRedirectLoginAppState>> {
    console.log('handling Redirect with auth0 SPA client')
    return this.client.handleRedirectCallback()
  }

  async getUser(): Promise<User> {
    const user = await this.client.getUser<User>()
    return user as User
  }

  async isAuthenticated(): Promise<boolean> {
    return this.client.isAuthenticated()
  }

  async getAccessToken(): Promise<GetTokenSilentlyVerboseResponse> {
    return this.client.getTokenSilently({ detailedResponse: true })
  }

  async getRoles(): Promise<string[]> {
    const userInfo = await this.getUser()
    return userInfo[`${window.env.AUTH0_AUDIENCE}/roles`]
  }

  async getScopes(): Promise<string[]> {
    const accessToken = await this.getAccessToken()
    const permissions = accessToken.scope?.split(' ') || []
    return permissions
  }

  async getClientsPermissions(): Promise<IClientsPermissions> {
    const permissions = await this.getScopes()
    return {
      canRead: permissions.some((permission) => permission === READ_CLIENTS || permission === UPDATE_CLIENTS),
      canUpdate: permissions.includes(UPDATE_CLIENTS),
      canDelete: permissions.includes(DELETE_CLIENTS),
    }
  }
}
